import { SystemInlineBanner, Text } from "@clipboard-health/ui-components";
import { isDefined, toError } from "@clipboard-health/util-ts";
import { Link } from "@mui/material";
import { useToast } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { openInBrowser } from "@src/appV2/NativeAdapters/browser.adapter";
import { SupportArticles, SupportContext } from "@src/appV2/support/constants";
import { useSupportLinks } from "@src/appV2/support/hooks/useSupportLinks";
import { type Worker } from "@src/appV2/Worker/api/types";

interface ICAgreementBannersProps {
  showAgreementScrolledErrorBanner: boolean;
  showConfirmationErrorBanner: boolean;
  worker: Worker;
}

export function ICAgreementBanners(props: ICAgreementBannersProps) {
  const { showAgreementScrolledErrorBanner, showConfirmationErrorBanner, worker } = props;

  const { howToContactSupportLink } = useSupportLinks();
  const { showErrorToast } = useToast();

  const handleContactSupport = async () => {
    try {
      await openInBrowser({ url: howToContactSupportLink });
    } catch (error) {
      logError(APP_V2_USER_EVENTS.OPEN_AGREEMENT_SUPPORT_ARTICLE_ERROR, {
        error: toError(error),
        metadata: {
          workerId: worker.userId,
          errorMessage: toError(error).message,
        },
      });

      // This should rarely happen, but we want to show a toast to the user if it does.
      showErrorToast(
        `Error opening agreement support article. Please manually go to the link: ${howToContactSupportLink}`
      );
      return;
    }

    logEvent(APP_V2_USER_EVENTS.OPENED_SUPPORT_ARTICLE, {
      articleLink: howToContactSupportLink,
      articleName: SupportArticles.HOW_TO_CONTACT_SUPPORT,
      userId: worker.userId,
      supportContext: SupportContext.IC_AGREEMENT_ERROR_ALERT,
    });
  };

  function getSupportText() {
    if (showAgreementScrolledErrorBanner && showConfirmationErrorBanner) {
      return "You must scroll to the bottom of the page before signing and click the checkbox to confirm and proceed. Having trouble?";
    }

    if (showAgreementScrolledErrorBanner) {
      return "You must scroll to the bottom of the page before signing. Having trouble?";
    }

    if (showConfirmationErrorBanner) {
      return "You must click the checkbox to confirm and proceed. Having trouble?";
    }

    return undefined;
  }

  const supportText = getSupportText();

  if (isDefined(supportText)) {
    return (
      <SystemInlineBanner color="error" iconType="triangle-exclamation" sx={{ marginTop: 6 }}>
        <Text variant="body2" color="inherit">
          {supportText}&nbsp;
          <Link
            variant="body2"
            sx={{
              color: "inherit",
              textDecorationColor: "inherit",
              ":hover": {
                color: "inherit",
                textDecoration: "underline",
              },
            }}
            onClick={handleContactSupport}
          >
            Contact our support team
          </Link>
          .
        </Text>
      </SystemInlineBanner>
    );
  }

  return null;
}
